import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000';

// Helper to get auth token
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Helper to create auth header
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

// Helper to check if user has admin-level access
const hasAdminAccess = () => {
  try {
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    return permissions.some(p => p.resource === 'user' && p.action === 'view');
  } catch (error) {
    console.error('Error checking permissions:', error);
    return false;
  }
};

// Helper to check if user can access a specific resource
// Checks for EITHER the specific permission OR dashboard.view permission
const canAccessResource = (resource, action = 'view') => {
  try {
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    return permissions.some(p =>
      (p.resource === resource && p.action === action) ||
      (p.resource === 'dashboard' && p.action === 'view')
    );
  } catch (error) {
    console.error('Error checking resource permission:', error);
    return false;
  }
};

// Wrapper for safe API calls with permission checking
// Returns: { success: boolean, data?: any, error?: string, message?: string }
const safeFetch = async (apiFunction, requiredPermission) => {
  // Check permission first
  if (!canAccessResource(requiredPermission.resource, requiredPermission.action)) {
    return {
      success: false,
      error: 'NO_PERMISSION',
      message: `You need ${requiredPermission.resource}.${requiredPermission.action} permission to view this data`
    };
  }

  // Attempt API call
  try {
    const data = await apiFunction();
    return { success: true, data };
  } catch (error) {
    // Check if it's a permission error
    if (error.response?.status === 403 || error.error === 'Permission denied') {
      return {
        success: false,
        error: 'NO_PERMISSION',
        message: error.message || 'You do not have permission to view this data'
      };
    }

    // Other errors (network, 500, etc.)
    return {
      success: false,
      error: 'API_ERROR',
      message: error.message || 'Failed to load data'
    };
  }
};

// Get dashboard summary statistics
// Returns partial data based on user's permissions
export const getDashboardSummary = async () => {
  // Fetch each stat independently with its own permission check
  // Use Promise.allSettled so one failure doesn't block others
  const [vehicles, users, trackers, contacts] = await Promise.allSettled([
    // Vehicles - check bookmark.view permission
    canAccessResource('bookmark', 'view')
      ? axios.get(`${API_URL}/auth/bookmarks`, getAuthHeaders())
      : Promise.reject({ error: 'NO_PERMISSION' }),

    // Users - check user.view permission
    canAccessResource('user', 'view')
      ? axios.get(`${API_URL}/auth/users`, getAuthHeaders())
      : Promise.reject({ error: 'NO_PERMISSION' }),

    // Trackers - check tracker.view permission
    canAccessResource('tracker', 'view')
      ? axios.get(`${API_URL}/api/trackers`, getAuthHeaders())
      : Promise.reject({ error: 'NO_PERMISSION' }),

    // Contact stats - check contact_message.view permission
    canAccessResource('contact_message', 'view')
      ? axios.get(`${API_URL}/auth/contact/messages/stats`, getAuthHeaders())
      : Promise.reject({ error: 'NO_PERMISSION' })
  ]);

  // Process results - return null for sections without permission
  return {
    totalVehicles: vehicles.status === 'fulfilled'
      ? (Array.isArray(vehicles.value.data)
          ? vehicles.value.data.length
          : (vehicles.value.data.bookmarks ? vehicles.value.data.bookmarks.length : 0))
      : null,

    totalUsers: users.status === 'fulfilled'
      ? (Array.isArray(users.value.data) ? users.value.data.length : 0)
      : null,

    totalTrackers: trackers.status === 'fulfilled'
      ? (trackers.value.data.trackers?.length || 0)
      : null,

    contactStats: contacts.status === 'fulfilled'
      ? contacts.value.data
      : null
  };
};

// Get vehicle type distribution
export const getVehicleTypeStats = async () => {
  return safeFetch(
    async () => {
      const response = await axios.get(`${API_URL}/auth/bookmarks`, getAuthHeaders());

      // Handle different response formats
      let bookmarks = [];
      if (Array.isArray(response.data)) {
        bookmarks = response.data;
      } else if (response.data.bookmarks && Array.isArray(response.data.bookmarks)) {
        bookmarks = response.data.bookmarks;
      }

      // Count by vehicle type (1=Motorcycle, 2=Khekeh)
      const counts = { motorcycle: 0, khekeh: 0, other: 0 };
      bookmarks.forEach(bookmark => {
        if (bookmark.vehicleType === 1) {
          counts.motorcycle++;
        } else if (bookmark.vehicleType === 2) {
          counts.khekeh++;
        } else {
          counts.other++;
        }
      });

      return [
        { id: 0, value: counts.motorcycle, label: 'Motorcycles' },
        { id: 1, value: counts.khekeh, label: 'Khekeh' }
      ].filter(item => item.value > 0); // Only return types with data
    },
    { resource: 'bookmark', action: 'view' }
  );
};

// Get user role distribution
export const getUserRoleStats = async () => {
  return safeFetch(
    async () => {
      const response = await axios.get(`${API_URL}/auth/users`, getAuthHeaders());
      const users = Array.isArray(response.data) ? response.data : [];

      // Count by role
      const roleCounts = {};
      users.forEach(user => {
        const role = user.role || 'Unknown';
        roleCounts[role] = (roleCounts[role] || 0) + 1;
      });

      return Object.entries(roleCounts).map(([role, count], idx) => ({
        id: idx,
        value: count,
        label: role
      }));
    },
    { resource: 'user', action: 'view' }
  );
};

// Get vehicles per user
export const getVehiclesPerUser = async () => {
  return safeFetch(
    async () => {
      const [bookmarksRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/auth/bookmarks`, getAuthHeaders()),
        axios.get(`${API_URL}/auth/users`, getAuthHeaders())
      ]);

      // Handle different response formats
      let bookmarks = [];
      if (Array.isArray(bookmarksRes.data)) {
        bookmarks = bookmarksRes.data;
      } else if (bookmarksRes.data.bookmarks && Array.isArray(bookmarksRes.data.bookmarks)) {
        bookmarks = bookmarksRes.data.bookmarks;
      }

      const users = Array.isArray(usersRes.data) ? usersRes.data : [];

      // Count vehicles per user
      const userVehicles = {};
      bookmarks.forEach(bookmark => {
        const userId = bookmark.user_id;
        if (userId) {
          userVehicles[userId] = (userVehicles[userId] || 0) + 1;
        }
      });

      // Map to user data and sort by count
      return Object.entries(userVehicles)
        .map(([userId, count]) => {
          const user = users.find(u => u.id === parseInt(userId));
          return {
            username: user?.username || user?.firstname || `User ${userId}`,
            count
          };
        })
        .sort((a, b) => b.count - a.count)
        .slice(0, 10); // Top 10 users
    },
    { resource: 'user', action: 'view' }
  );
};

// Get contact message trend (last 7 days)
export const getContactMessageTrend = async () => {
  return safeFetch(
    async () => {
      const response = await axios.get(
        `${API_URL}/auth/contact/messages`,
        getAuthHeaders()
      );

      const messages = Array.isArray(response.data) ? response.data : [];

      // Get last 7 days
      const today = new Date();
      const last7Days = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        last7Days.push(date.toISOString().split('T')[0]);
      }

      // Count messages per day
      const messageCounts = {};
      last7Days.forEach(date => {
        messageCounts[date] = 0;
      });

      messages.forEach(message => {
        const messageDate = new Date(message.createdAt).toISOString().split('T')[0];
        if (messageCounts.hasOwnProperty(messageDate)) {
          messageCounts[messageDate]++;
        }
      });

      return {
        labels: last7Days.map(date => {
          const d = new Date(date);
          return `${d.getMonth() + 1}/${d.getDate()}`;
        }),
        data: last7Days.map(date => messageCounts[date])
      };
    },
    { resource: 'contact_message', action: 'view' }
  );
};

// Get command statistics (uses existing tracker stats endpoint)
export const getCommandStats = async () => {
  return safeFetch(
    async () => {
      const response = await axios.get(
        `${API_URL}/api/trackers/stats/commands`,
        getAuthHeaders()
      );
      return response.data;
    },
    { resource: 'tracker', action: 'view' }
  );
};

// Get verification statistics
export const getVerificationStats = async (startDate, endDate) => {
  return safeFetch(
    async () => {
      let url = `${API_URL}/api/bookmarks/verification-stats`;
      const params = new URLSearchParams();

      if (startDate) {
        params.append('startDate', startDate);
      }
      if (endDate) {
        params.append('endDate', endDate);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url, getAuthHeaders());
      return response.data.stats || [];
    },
    { resource: 'bookmark', action: 'view' }
  );
};
